import type { CityConfig } from "../city-template/types";

export const columbiaMoConfig: CityConfig = {
  city: "Columbia",
  state: "Missouri",
  stateAbbr: "MO",
  slug: "columbia-mo",
  route: "/cities/columbia-mo",
  title: "Emergency Passport Columbia, MO | Fast Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Columbia, MO — as fast as 24 hours. Fast passport renewal, same-day help, and lost passport replacement. A+ BBB rated, registered State Dept. courier serving Columbia and Mizzou students.",
  h1: "Emergency & Expedited Passport Services in Columbia, MO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or same-day help? We help Columbia and Central Missouri travelers get their urgent passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Columbia%20Missouri%20campus%20and%20downtown%20at%20golden%20hour%20with%20University%20of%20Missouri%20columns%20visible%2C%20historic%20buildings%20along%20Broadway%2C%20warm%20sunset%20light%20with%20amber%20and%20orange%20tones%2C%20college%20town%20atmosphere%20with%20tree-lined%20streets%2C%20professional%20cityscape%20photography%2C%20clean%20modern%20urban%20scene%20with%20academic%20charm%2C%20no%20visible%20text%20or%20people%2C%20American%20Midwest%20university%20town&width=1800&height=700&seq=col-mo-hero-001&orientation=landscape",
  heroImageAlt: "Columbia Missouri downtown at golden hour with University of Missouri columns and Broadway",
  geoPosition: "38.9517;-92.3341",
  geoPlacename: "Columbia, Missouri",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, Chicago, IL 60605",
    distance: "Chicago is the nearest regional passport agency — approximately 7 hours from Columbia by car. Fast Passport Center eliminates the need for this drive with our registered courier service.",
  },
  nearbyCitiesZips: [
    { name: "Downtown Columbia", zips: "65201" },
    { name: "University of Missouri", zips: "65211" },
    { name: "Jefferson City", zips: "65101, 65109" },
    { name: "Moberly", zips: "65270" },
    { name: "Fulton", zips: "65251" },
    { name: "Boonville", zips: "65233" },
    { name: "Mexico", zips: "65265" },
    { name: "Sedalia", zips: "65301" },
    { name: "Kirksville", zips: "63501" },
    { name: "Hannibal", zips: "63401" },
    { name: "Rolla", zips: "65401" },
    { name: "Warrensburg", zips: "64093" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Columbia departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Kansas City", route: "/cities/kansas-city" },
    { name: "Emergency passport help in St. Louis", route: "/cities/st-louis" },
    { name: "Urgent passport services in Springfield", route: "/cities/springfield-mo" },
    { name: "Expedited passport services in Missouri", route: "/states/missouri" },
    { name: "Fast passport renewal for students", route: "/states/missouri" },
    { name: "Passport courier services in Des Moines", route: "/cities/des-moines" },
    { name: "Rush passport help in Indianapolis", route: "/cities/indianapolis" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Columbia remotely via secure courier. We do not have a walk-in office in Columbia — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Columbia travelers.",
  fpcOfficeDistance: "Serving all of Boone County and Central Missouri",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Columbia and Central Missouri. From downtown Columbia to the University of Missouri campus, from Jefferson City to Moberly — we help Columbia travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Columbia neighborhoods and nearby cities including Jefferson City, Moberly, Fulton, Boonville, and Sedalia. If you are in Boone County or Central Missouri, we have you covered.",
  howItWorksIntro: "Most Columbia travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait for standard mail. Our emergency and expedited passport process is designed for Central Missouri travelers who need fast, reliable results.",
  mistakesIntro: "With Mizzou students and faculty traveling internationally for research and study abroad, Columbia travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Columbia travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Columbia? Here are the most common ones we hear from Boone County travelers.",
  statePageRoute: "/states/missouri",
  statePageName: "Missouri",
  dmvWebsite: "https://dor.mo.gov/motor-vehicle",
  dmvLabel: "Missouri Motor Vehicle — Official Website",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Missouri Vital Records (Birth, Death, Marriage)", href: "https://health.mo.gov/data/vitalrecords/" },
    { label: "Boone County Clerk Vital Records", href: "https://www.showmeboone.com/clerk/" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Columbia travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Emily W.",
      location: "Columbia, MO",
      rating: 5,
      text: "My study abroad program to Italy started in 5 days and I realized my passport was expired. Fast Passport Center got me a new one in 48 hours. Saved my semester abroad.",
      service: "Emergency Passport",
    },
    {
      name: "Dr. James C.",
      location: "University of Missouri",
      rating: 5,
      text: "I needed to renew my passport for a conference in Tokyo. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for faculty.",
      service: "Passport Renewal",
    },
    {
      name: "Rachel T.",
      location: "Jefferson City, MO",
      rating: 5,
      text: "Lost my passport before a family trip to the Caribbean. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Mark D.",
      location: "Moberly, MO",
      rating: 5,
      text: "As a frequent international traveler from Central Missouri, I can't afford passport delays. Fast Passport Center has been my go-to for years. Reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "col-mo-1",
      name: "Columbia Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "511 E Walnut St",
      city: "Columbia",
      state: "MO",
      zip: "65201",
      phone: "(573) 874-6321",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Columbia",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "col-mo-2",
      name: "University of Missouri Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 E Walnut St",
      city: "Columbia",
      state: "MO",
      zip: "65211",
      phone: "(573) 882-1111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Mizzou campus",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours. Popular with students.",
      icon: "ri-mail-line",
    },
    {
      id: "col-mo-3",
      name: "Jefferson City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1315 Eastland Dr",
      city: "Jefferson City",
      state: "MO",
      zip: "65101",
      phone: "(573) 635-6114",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Missouri State Capitol",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for state capital residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "col-mo-4",
      name: "Columbia City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "701 E Broadway",
      city: "Columbia",
      state: "MO",
      zip: "65201",
      phone: "(573) 874-7111",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from downtown Columbia",
      website: "https://www.como.gov/",
      websiteLabel: "Columbia City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "col-mo-5",
      name: "Sedalia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2401 S Limit Ave",
      city: "Sedalia",
      state: "MO",
      zip: "65301",
      phone: "(660) 826-5300",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1 mile from downtown Sedalia",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Pettis County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "col-mo-6",
      name: "Boone County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "801 E Walnut St",
      city: "Columbia",
      state: "MO",
      zip: "65201",
      phone: "(573) 886-4295",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from downtown Columbia",
      website: "https://www.showmeboone.com/",
      websiteLabel: "Boone County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 7-hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Columbia photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Columbia acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Columbia applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Columbia families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Columbia travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Columbia." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Columbia address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Columbia?",
      a: "Fast Passport Center offers emergency passport processing in Columbia in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Columbia travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Columbia?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to Columbia is in Chicago, a 7-hour drive. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Columbia residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Columbia?",
      a: "Fast Passport Center offers fast passport renewal in Columbia with no office visit required. We serve all Columbia neighborhoods including the University of Missouri campus, downtown, and surrounding areas. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Columbia?",
      a: "The fastest way to get an urgent passport in Columbia is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in Columbia?",
      a: "With Fast Passport Center's emergency courier service, Columbia travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Chicago Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Columbia?",
      a: "Popular options include the Columbia Main Post Office (65201), University of Missouri Post Office (65211), Jefferson City Post Office (65101), and Columbia City Clerk (65201). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Jefferson City or Central Missouri?",
      a: "We serve all of Central Missouri including Jefferson City, Moberly, Fulton, Boonville, and Sedalia. Our overnight shipping labels work from any UPS location, so your location in Central Missouri doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Columbia?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Columbia?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Columbia offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Columbia?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Columbia?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Columbia families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Columbia", address: "2012 W Broadway, Columbia, MO 65203", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — Columbia", address: "1710 N Providence Rd, Columbia, MO 65202", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — Columbia", address: "3000 Interstate 70 Dr SE, Columbia, MO 65201", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.office.fedex.com/" },
    { name: "UPS Store — Jefferson City", address: "1411 Southwest Blvd, Jefferson City, MO 65109", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Columbia", address: "1200 Grindstone Pkwy, Columbia, MO 65201", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/photo" },
    { name: "Rite Aid — Columbia", address: "1000 E Broadway, Columbia, MO 65201", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/photo" },
    { name: "Postal Annex — Sedalia", address: "2401 S Limit Ave, Sedalia, MO 65301", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — Columbia", address: "3800 Buttonwood Dr, Columbia, MO 65201", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/photo" },
  ],
  dmvLocations: [
    {
      name: "DMV — Columbia",
      address: "3702 Buttonwood Dr, Columbia, MO 65201",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Jefferson City",
      address: "1805 Industrial Dr, Jefferson City, MO 65109",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Moberly",
      address: "901 S Morley St, Moberly, MO 65270",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Sedalia",
      address: "3700 W Broadway, Sedalia, MO 65301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Pettis County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "COU",
      name: "Columbia Regional Airport",
      distance: "12 miles from downtown Columbia",
      address: "11210 S Airport Dr, Columbia, MO 65201",
      note: "The primary airport for Columbia and Central Missouri. Non-stop flights to Chicago, Dallas, and Denver with international connections. Popular with Mizzou students and faculty.",
      flightUrl: "https://www.flycolumbiamo.com/",
      flightLabel: "COU Airport Info",
      featured: true,
    },
    {
      code: "STL",
      name: "St. Louis Lambert International Airport",
      distance: "120 miles from Columbia",
      address: "10701 Lambert International Blvd, St. Louis, MO 63145",
      note: "Major international airport accessible from Columbia via I-70. Extensive domestic and international flight options.",
      flightUrl: "https://www.flystl.com/",
      flightLabel: "STL Airport Info",
      featured: false,
    },
  ],
};